"use client";

import { Info } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * A footer button that opens a small panel above it: the desktop's and the
 * tablet's company information, a phone's More. It toggles on press, and a
 * press anywhere outside it or Escape closes it (Escape hands focus back to
 * the button), so a touch screen never has to find a close control.
 */
export function LandingFooterDisclosure({
  id,
  label,
  trigger,
  triggerClassName,
  panelClassName = "",
  className = "",
  children,
}: {
  id: string;
  /** The panel's name, and the button's where `trigger` is an icon. */
  label: string;
  /** The button's content; text names the button itself. */
  trigger: ReactNode;
  triggerClassName: string;
  /** Placement and width of the panel. */
  panelClassName?: string;
  /** The wrapper's layout in its row. */
  className?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={`relative ${className}`}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          setOpen(false);
          window.requestAnimationFrame(() => triggerRef.current?.focus());
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={typeof trigger === "string" ? undefined : label}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((current) => !current)}
        className={triggerClassName}
      >
        {trigger}
      </button>
      <div
        id={id}
        role="region"
        aria-label={label}
        hidden={!open}
        className={`absolute bottom-full z-30 mb-2 rounded-lg border border-cyan-400/20 bg-[#020813]/95 p-3 text-start text-[11px] text-white/75 shadow-[0_8px_28px_rgba(0,229,255,0.15)] backdrop-blur-sm ${panelClassName}`}
      >
        {children}
      </div>
    </div>
  );
}

/** SmartManager LLC's address and contact, as every footer gives it. */
export function LandingCompanyDetails({ t }: { t: (source: string) => string }) {
  return (
    <>
      <h2 className="mb-1 text-cyan-50">SmartManager LLC</h2>
      <p>480 Boynton Ave</p>
      <p>San Jose, California 95117</p>
      <p>{t("United States")}</p>
      <p className="flex items-center gap-1">
        <span>{t("Email")}:</span>
        <a
          className="inline-flex min-h-11 items-center underline underline-offset-2 hover:text-cyan-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-200"
          href="mailto:jonathan@smartmanager.ai"
        >
          jonathan@smartmanager.ai
        </a>
      </p>
    </>
  );
}

/** The info button beside the copyright on the desktop's and a tablet's footer. */
export function LandingCompanyInfo({ t, className }: { t: (source: string) => string; className?: string }) {
  return (
    <LandingFooterDisclosure
      className={className}
      id="company-information-panel"
      label={t("Company information")}
      trigger={<Info aria-hidden="true" className="h-3 w-3" />}
      triggerClassName="inline-flex h-11 w-11 items-center justify-center rounded-full text-white/65 transition-colors hover:text-white/90 active:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-200"
      panelClassName="right-0 w-64"
    >
      <LandingCompanyDetails t={t} />
    </LandingFooterDisclosure>
  );
}
